import React from 'react';
import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import { Home, NavigateNext } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { DASHBOARD_SECTIONS } from './DashboardRedesign/dashboardConfig';

/**
 * Resolves the section title and module name for a given pathname by walking
 * the dashboard config. Returns { sectionTitle, moduleName } or nulls when
 * the path is not found in the config (e.g. sub-routes handled via currentSection prop).
 */
function resolveFromConfig(pathname) {
  for (const section of Object.values(DASHBOARD_SECTIONS)) {
    const match = section.modules.find(m => m.path === pathname);
    if (match) {
      return { sectionTitle: section.title, moduleName: match.name };
    }
  }
  return { sectionTitle: null, moduleName: null };
}

const DashboardBreadcrumbs = ({ currentSection = null }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isOnDashboard = location.pathname === '/dashboard';

  if (isOnDashboard && !currentSection) {
    return null;
  }

  const { sectionTitle, moduleName } = resolveFromConfig(location.pathname);

  return (
    <Box sx={{ mb: 2 }}>
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        sx={{ '& .MuiBreadcrumbs-ol': { alignItems: 'center' } }}
      >
        {/* Dashboard — always first */}
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/dashboard')}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            cursor: 'pointer', textDecoration: 'none', color: 'primary.main',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <Home fontSize="small" />
          Dashboard
        </Link>

        {/* Section (e.g. "Services & Payments") */}
        {sectionTitle && (
          <Typography variant="body2" color="text.secondary">
            {sectionTitle}
          </Typography>
        )}

        {/* Module (e.g. "Task Templates") — current page, bold */}
        {moduleName && (
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
            {moduleName}
          </Typography>
        )}

        {/* Fallback: manually passed section name (used by sub-routes or maid portal) */}
        {!moduleName && currentSection && (
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
            {currentSection}
          </Typography>
        )}
      </Breadcrumbs>
    </Box>
  );
};

export default DashboardBreadcrumbs;
